import {
  Component, OnInit, OnDestroy, Input, Output, EventEmitter,
  ChangeDetectorRef, ChangeDetectionStrategy,
  Inject, Renderer2, HostBinding, ElementRef, QueryList,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { ResizeEvent } from 'angular-resizable-element';
import * as _ from 'lodash';

import { BrandService } from 'shared/brand/brand.service';
import { Config } from 'shared/config/env.config';
import { PermissionsService } from 'shared/permissions/permissions.service';

import { SideDrawerService } from './sidedrawer.service';
import { MENU_STATUS, minWidth, maxWidth } from './sidedrawer-config';
import { routesData } from './routes-data';
import {trigger, state, style, transition, animate} from '@angular/animations';
import {takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'sidedrawer-component',
  templateUrl: 'sidedrawer.component.html',
  styleUrls: ['sidedrawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('collapseAnimation', [
      state(MENU_STATUS.COLLAPSED, style({width: `${minWidth}px`})),
      state(MENU_STATUS.EXPANDED, style({width: `${maxWidth}px`})),
      transition(`* => ${MENU_STATUS.COLLAPSED}`, [
        animate('0.3s ease-out')
      ]),
      transition(`* => ${MENU_STATUS.EXPANDED}`, [
        animate('0.3s ease-out')
      ]),
    ]),
  ],
})
export class SideDrawerComponent implements OnInit, OnDestroy {
  routes: any[];
  dragInProcess: boolean = false;
  animationInProcess: boolean = false;
  noTitles: boolean;
  currentWidth: number;
  isComponentActive: boolean = true;

  // active item animations
  activeIndex: number = 0;
  isLogoActive: boolean = false;
  isImportExportActive: boolean = false;

  @Input() disabled: boolean = false;

  @HostBinding('class.sidedrawer-wrap') isSidedrawerWrap = true;

  @HostBinding('class.no-titles') get isNoTitles() {
    return this.noTitles;
  }

  @HostBinding('class.collapsed') get collapsedClass() {
    return this.collapsed && !this.animationInProcess && !this.dragInProcess;
  }

  @Output() sidedrawerCollapsed: EventEmitter<any> = new EventEmitter();

  constructor(
    private ref: ChangeDetectorRef,
    private router: Router,
    private brandService: BrandService,
    private permissionsService: PermissionsService,
    private sidedrawerService: SideDrawerService,
    @Inject(DOCUMENT) private document: any,
    private renderer: Renderer2,
  ) {
    this.router.events
     .pipe(takeWhile(() => this.isComponentActive))
     .subscribe(event => {
       if (event instanceof NavigationEnd) {
         // if no button was clicked
         this.setActive();
       }
       this.ref.markForCheck();
     });
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

   ngOnInit() {
     this.noTitles = this.collapsed;
     this.sidedrawerCollapsed.emit(this.collapsed);
     this.routes = this.getAvailableRoutes(routesData);
     this.permissionsService.permissionsChange
       .pipe(takeWhile(() => this.isComponentActive))
       .subscribe(() => {
         this.routes = this.getAvailableRoutes(routesData);
         this.ref.markForCheck();
       });
     this.setActive();
     this.ref.markForCheck();
   }

   ngOnDestroy() {
     this.isComponentActive = false;
     this.ref.markForCheck();
   }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get allowGlobalAcccess(): boolean {
    return this.hasPermission(['admin', 'catalog-edit']);
  }

  get animationState(): string {
    if (this.dragInProcess) {
      return null;
    } else {
      return this.collapsed ? MENU_STATUS.COLLAPSED : MENU_STATUS.EXPANDED;
    }
  }

  get collapsed(): boolean {
    return this.sidedrawerService.getCollapsed();
  }

  set collapsed(collapsed: boolean) {
    this.sidedrawerService.setCollapsed(collapsed);
    this.sidedrawerService.setWidth(collapsed ? minWidth : maxWidth);
    this.ref.markForCheck();
  }

  get getActivePosition(): string {
    return `${81 * this.activeIndex}px`;
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  public getBrandLogo() {
    return Config.BACKEND + '/brand-logo/' + this.brandService.getBrand() + '?v=' + Date.now();
  }

  public hasPermission(permission: Array<string>|string, requireAll: boolean= false): boolean {
    return this.permissionsService.hasPermission(permission, requireAll);
  }

  public isActive(link: string) {
    return this.router.url.indexOf(`/${link}`) >= 0;
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onResizeStart(event: ResizeEvent) {
    this.renderer.addClass(document.documentElement, 'sidedrawer-dragging');
  }

  onResizeEnd(event: ResizeEvent) {
    this.renderer.removeClass(document.documentElement, 'sidedrawer-dragging');
    this.currentWidth = _.get(event, 'rectangle.width');
    const delta = _.get(event, 'edges.right');
    this.dragInProcess = false;

    // don't change collapsed if it's just click
    if (delta === 0) {
      this.ref.markForCheck();
      return;
    }

    if (!this.collapsed) {
      this.collapsed = (delta <= -30);
    } else {
      this.collapsed = (delta <= 30);
    }
    this.ref.markForCheck();
  }

  onResizing(event: ResizeEvent) {
    this.dragInProcess = true;
    const width = _.get(event, 'rectangle.width');
    if (width < minWidth) {
      this.sidedrawerService.setWidth(minWidth);
    } else if (width > maxWidth) {
      this.sidedrawerService.setWidth(maxWidth);
    } else {
      this.sidedrawerService.setWidth(width);
    }
    this.noTitles = (width <= 120);
    this.ref.markForCheck();
  }

  onAnimationStart(event: any) {
    this.currentWidth = null;
    this.animationInProcess = true;
    this.renderer.addClass(document.documentElement, 'sidedrawer-animation-in-process');
    this.ref.markForCheck();
  }

  onAnimationEnd(event: any) {
    this.renderer.removeClass(document.documentElement, 'sidedrawer-animation-in-process');
    // emit collapsed only after animation finished
    this.animationInProcess = false;
    this.sidedrawerService.setWidth(null);
    if (event.toState) {
      if (this.collapsed) {
        this.renderer.addClass(document.documentElement, 'sidedrawer-collapsed');
        this.noTitles = true;
      } else {
        this.renderer.removeClass(document.documentElement, 'sidedrawer-collapsed');
        this.noTitles = false;
      }
    }
    this.ref.markForCheck();
  }

  onMenuItemClick(item: any, index: number) {
    if (item.name === 'receivers') {
      window.open('https://apps.semadata.org/#SupplierRequests', '_blank');
      return;
    }
    this.activeIndex = index;
    this.isLogoActive = false;
    this.isImportExportActive = false;
    this.ref.markForCheck();
    _.delay(() => {
      this.router.navigate([item.route]);
    }, 300);
  }

  onLogoClick() {
    this.isLogoActive = true;
    this.isImportExportActive = false;
    this.ref.markForCheck();
    if (this.allowGlobalAcccess) {
      this.router.navigate(['/global-brand-information/product-groups']);
    }
  }

  onImportExportClick() {
    this.isLogoActive = false;
    this.isImportExportActive = true;
    this.ref.markForCheck();
    _.delay(() => {
      this.router.navigate(['/export-manager']);
    }, 300);
  }

  private setActive() {
    const url = this.router.url;

    const index = this.activeIndex = _.findIndex(this.routes, route => {
      return (url.indexOf(`/${route.route}`) >= 0);
    });
    if (index >= 0 && index !== this.activeIndex) {
      this.activeIndex = index;
    }

    this.isImportExportActive = (
      url.indexOf('/import-manager') >= 0 ||
      url.indexOf('/export-manager') >= 0
    );

    this.isLogoActive = (
      url.indexOf('/global-brand-information') >= 0 ||
      url.indexOf('/account-information') >= 0
    );

    this.ref.markForCheck();
  }

  private getAvailableRoutes(routes: any) {
    return _.filter(routes, (route: any) => {
      return (route.allow) ? this.hasPermission(route.allow) : true;
    });
  }
}
